import type React from "react";
import { useState } from "react";
import type { ValidationFinding, ValidationResult } from "@/api/validatorApi";
import { SegmentPopover } from "./SegmentPopover";

function splitSegments(raw: string): string[] {
  const parts: string[] = [];
  let cur = "";
  for (let i = 0; i < raw.length; i++) {
    const c = raw[i];
    if (c === "?" && i + 1 < raw.length) {
      cur += c + raw[i + 1];
      i++;
      continue;
    }
    if (c === "'") {
      cur += c;
      parts.push(cur);
      cur = "";
      continue;
    }
    cur += c;
  }
  if (cur) parts.push(cur);
  return parts;
}

interface Props {
  raw: string;
  result: ValidationResult | null;
}

/**
 * Segment-Highlighter mit Fehler-Inlay + Hover-Popover.
 *
 * - Pro fehlerhafte / warnende Segment-Zeile wird das Segment rot/gelb
 *   eingefärbt UND die Findings-Liste direkt darunter als Inline-Block
 *   gerendert (immer sichtbar, kein Hover nötig).
 * - Hover-Popover via reines CSS (`.edi-seg .edi-popover`) — keine
 *   Native-`title`-Tooltips mehr (langsam, kein Styling).
 */
export function EdifactHighlight({ raw, result }: Props) {
  // Aktuell gehovertes Segment -- single source of truth fürs Portal-Popover.
  // Wir rendern nur EINE Popover-Instanz pro EdifactHighlight, nicht eine pro
  // Segment, damit der Portal-Mount/Unmount-Wechsel sauber bleibt.
  const [hover, setHover] = useState<{
    anchor: HTMLElement;
    findings: ValidationFinding[];
    severity: "error" | "warn";
  } | null>(null);

  const errorMap = new Map<number, ValidationFinding[]>();
  const warnMap = new Map<number, ValidationFinding[]>();
  for (const e of result?.errors ?? []) {
    const arr = errorMap.get(e.location.segment_index) ?? [];
    arr.push(e);
    errorMap.set(e.location.segment_index, arr);
  }
  for (const w of result?.warnings ?? []) {
    const arr = warnMap.get(w.location.segment_index) ?? [];
    arr.push(w);
    warnMap.set(w.location.segment_index, arr);
  }
  const vorgangStarts = new Map<number, string>();
  for (const vr of result?.vorgaenge ?? []) {
    vorgangStarts.set(
      vr.vorgang.start_segment_index,
      `Vorgang ${vr.vorgang.index + 1} (${vr.vorgang.vorgang_id ?? vr.vorgang.index + 1})`,
    );
  }

  const parts = splitSegments(raw.trim());
  let segIdx = -1;
  let inMessage = false;

  return (
    <div
      className="edi-highlight"
      style={{
        fontFamily: "var(--sapFontMonoFamily, monospace)",
        fontSize: "0.85rem",
        lineHeight: 1.7,
        background: "var(--sapShell_Background, #1a1f24)",
        color: "var(--sapShell_TextColor, #d4d4d4)",
        padding: "var(--s-3)",
        borderRadius: "var(--sapElement_BorderCornerRadius, 4px)",
        overflowX: "auto",
        whiteSpace: "pre-wrap",
        wordBreak: "break-all",
        border: "1px solid var(--sapList_BorderColor)",
      }}
    >
      {parts.map((part, i) => {
        const stripped = part.trim();
        if (!stripped) return null;
        const tag = stripped.slice(0, 3);
        if (tag === "UNA" || tag === "UNB" || tag === "UNZ") {
          return (
            <div key={i} style={{ color: "var(--sapContent_LabelColor, #888)", opacity: 0.7 }}>
              {stripped}
            </div>
          );
        }
        if (tag === "UNH") {
          inMessage = true;
          segIdx = 0;
        } else if (inMessage) {
          segIdx++;
        }
        if (tag === "UNT") inMessage = false;

        const segLabel = vorgangStarts.get(segIdx);
        const errs = errorMap.get(segIdx);
        const warns = warnMap.get(segIdx);
        const severity: "error" | "warn" | null = errs ? "error" : warns ? "warn" : null;
        const findings = errs ?? warns ?? [];

        // Dezente Hintergrund-Tints statt vollflächiger Sap-Negative-/Critical-Farben.
        // Original-Lesbarkeit der EDI-Quelle bleibt erhalten, der Highlight ist nur ein
        // Hinweis -- Detail kommt im Hover-Popover.
        const segStyle: React.CSSProperties =
          severity === "error"
            ? {
                background: "rgba(255, 82, 82, 0.18)",
                borderLeft: "3px solid var(--sapNegativeColor, #ff5252)",
                paddingLeft: 6,
              }
            : severity === "warn"
              ? {
                  background: "rgba(255, 176, 0, 0.18)",
                  borderLeft: "3px solid var(--sapCriticalColor, #ffb000)",
                  paddingLeft: 6,
                }
              : {};

        return (
          <div key={i}>
            {segLabel && (
              <div
                style={{
                  color: "var(--sapInformativeColor, #569cd6)",
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  borderTop: "1px dashed var(--sapInformativeColor, #569cd6)",
                  paddingTop: 2,
                  marginTop: 6,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                }}
              >
                — {segLabel} —
              </div>
            )}
            <span
              className={severity ? `edi-seg edi-seg-${severity}` : undefined}
              style={{ ...segStyle, cursor: severity ? "help" : "default" }}
              onMouseEnter={
                severity
                  ? (e) =>
                      setHover({
                        anchor: e.currentTarget,
                        findings,
                        severity,
                      })
                  : undefined
              }
              onMouseLeave={severity ? () => setHover(null) : undefined}
            >
              {stripped}
            </span>
          </div>
        );
      })}
      {hover && (
        <SegmentPopover
          anchor={hover.anchor}
          findings={hover.findings}
          severity={hover.severity}
        />
      )}
    </div>
  );
}
